<template>
  <nav v-if="pagination && pagination.pageCount > 1">
    <ul class="flex items-center justify-center gap-1 mt-base">
      <!-- Previous -->
      <li class="w-12">
        <Transition>
          <NuxtLink
            v-if="pagination.page != 1"
            class="grid aspect-square place-items-center hover:underline hover:fill-dark transition-all rounded"
            :prefetch="true"
            :prefetched-class="'prefetched'"
            :to="getPageRoute(Math.max(1, pagination.page - 1))"
          >
            <svg
              class="h-[.8rem]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path
                d="M7.4 273.4C2.7 268.8 0 262.6 0 256s2.7-12.8 7.4-17.4l176-168c9.6-9.2 24.8-8.8 33.9 .8s8.8 24.8-.8 33.9L83.9 232 424 232c13.3 0 24 10.7 24 24s-10.7 24-24 24L83.9 280 216.6 406.6c9.6 9.2 9.9 24.3 .8 33.9s-24.3 9.9-33.9 .8l-176-168z"
              />
            </svg>
            <Head>
              <Link
                rel="prev"
                :href="getPageRoute(Math.max(1, pagination.page - 1), true)"
              />
            </Head>
          </NuxtLink>
        </Transition>
      </li>

      <!-- Page -->
      <li v-for="idx in pages" :key="`pagination-btn-page-${idx}`" class="w-12">
        <NuxtLink
          v-if="idx !== '...'"
          class="grid aspect-square place-items-center hover:underline hover:fill-dark transition-all rounded-full"
          :prefetch="true"
          :prefetched-class="'prefetched'"
          :class="{
            'bg-dark !text-light font-bold cursor-default':
              idx == pagination.page,
          }"
          :to="getPageRoute(parseInt(`${idx}`))"
        >
          {{ idx }}
        </NuxtLink>
        <div
          v-else
          class="grid aspect-square place-items-center transition-all rounded-full"
        >
          ...
        </div>
      </li>

      <!-- Next -->
      <li class="w-12">
        <Transition>
          <NuxtLink
            v-if="pagination.page != pagination.pageCount"
            class="grid aspect-square place-items-center hover:underline hover:fill-dark transition-all rounded"
            :prefetch="true"
            :prefetched-class="'prefetched'"
            :to="
              getPageRoute(Math.min(pagination.pageCount, pagination.page + 1))
            "
          >
            <svg
              class="h-[.8rem]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path
                d="M440.6 273.4c4.7-4.5 7.4-10.8 7.4-17.4s-2.7-12.8-7.4-17.4l-176-168c-9.6-9.2-24.8-8.8-33.9 .8s-8.8 24.8 .8 33.9L364.1 232 24 232c-13.3 0-24 10.7-24 24s10.7 24 24 24l340.1 0L231.4 406.6c-9.6 9.2-9.9 24.3-.8 33.9s24.3 9.9 33.9 .8l176-168z"
              />
            </svg>
            <Head>
              <Link
                rel="next"
                :href="
                  getPageRoute(
                    Math.min(pagination.pageCount, pagination.page + 1),
                    true,
                  )
                "
              />
            </Head>
          </NuxtLink>
        </Transition>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from "vue-router";
import { computed } from "#imports";

const route = useRoute();

const props = defineProps<{
  paginationId: string;
}>();
const { pagination } = useFilters(props.paginationId);

const pages = computed(() => {
  let currentPage = pagination.value.page;
  const totalPages = pagination.value.pageCount;

  const pages = [];

  // Ensure the current page is within the bounds
  currentPage = Math.max(1, Math.min(currentPage, totalPages));

  // Always include the first page
  pages.push(1);

  // Determine the start and end for the pages around the current page
  let startPage = Math.max(2, currentPage - 1);
  let endPage = Math.min(totalPages - 1, currentPage + 1);

  // Adjust if the current page is near the beginning or the end
  if (currentPage <= 3) {
    startPage = 2;
    endPage = Math.min(2, totalPages - 1);
  } else if (currentPage >= totalPages - 2) {
    startPage = Math.max(2, totalPages - 3);
    endPage = totalPages - 1;
  }

  // Include the calculated range of pages
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Always include the last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  // Use ellipses to indicate skipped pages
  const uniquePages = [];
  let lastPage = 0;
  for (const page of pages) {
    if (page - lastPage > 1) {
      uniquePages.push("...");
    }
    uniquePages.push(page);
    lastPage = page;
  }

  return uniquePages;
});

const page = useState(`page-idx-${props.paginationId}`, () => {
  return pagination.value.page;
});

const routePage = computed(() => {
  return parseInt(route.query.p as string) || 1;
});
watch(routePage, (val) => {
  page.value = val;
});

watchEffect(() => {
  pagination.value = {
    ...pagination.value,
    page: page.value,
  };

  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
});

// watch(
//   () => pagination.value.page,
//   () => {
//     //scroll to top
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   },
// );
// watch(
//   () => pagination.value.pageCount,
//   () => {
//     if (pagination.value.page > pagination.value.pageCount) {
//       // if the current page is higher than the new page count, go to the last page
//       navigateTo(getPageRoute(1));
//     }
//   },
// );
const router = useRouter();
// const paginationMeta = computed(() => pagination.value.pagination as PaginationMeta);
const getPageRoute = (
  idx: number,
  href: boolean = false,
): RouteLocationRaw | string => {
  const q = { ...route.query };
  q.p = idx.toString();
  const r: RouteLocationRaw = {
    name: `${route.name?.toString()}`,
    params: { ...route.params },
    query: { ...q },
  };
  if (href) {
    return router.resolve(r).href;
  }
  return r;
};
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.1s linear;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
